import React, { useState, useEffect } from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { ChevronLeft, Save, Plus, Play, Edit, Layers, ChevronRight, ChevronDown, FolderKanban, Building2, Calendar, Settings2, Briefcase } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { psAPI, companyAPI, controllingAPI, hcmAPI } from '../../../services/api';
import { toast } from 'sonner';

const ProjectForm = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const isEdit = Boolean(projectId);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [projectProfiles, setProjectProfiles] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);
  const [controllingAreas, setControllingAreas] = useState([]);
  const [profitCenters, setProfitCenters] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  // WBS related state (only for edit mode)
  const [wbsElements, setWbsElements] = useState([]);
  const [wbsProfiles, setWbsProfiles] = useState([]);
  const [showWBSDialog, setShowWBSDialog] = useState(false);
  const [editWBS, setEditWBS] = useState(false);
  const [expandedWBS, setExpandedWBS] = useState(new Set());

  const [formData, setFormData] = useState({
    project_id: '',
    name: '',
    description: '',
    project_profile_id: '',
    company_code_id: '',
    controlling_area_id: '',
    business_area_id: '',
    profit_center_id: '',
    plant_id: '',
    responsible_person_id: '',
    applicant_id: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 90*24*60*60*1000).toISOString().split('T')[0],
    customer_id: '',
    sales_order_id: '',
    currency: 'USD',
    is_statistical: false,
    investment_program_id: '',
    investment_reason_id: '',
    priority: 3,
    system_status: 'CREATED'
  });

  const [wbsFormData, setWbsFormData] = useState({
    wbs_element_id: '', name: '', description: '', project_id: projectId,
    parent_wbs_id: '', level: 1, sort_key: '',
    wbs_profile_id: '', is_account_assignment_element: true,
    is_planning_element: true, is_billing_element: false,
    start_date: '', end_date: ''
  });

  useEffect(() => {
    fetchLookupData();
    if (isEdit) fetchProject();
  // eslint-disable-next-line
  }, [projectId]);

  const fetchLookupData = async () => {
    try {
      const [profilesRes, ccRes, caRes, pcRes, empRes, wbsProfilesRes] = await Promise.all([
        psAPI.getProjectProfiles(),
        companyAPI.getCompanyCodes(),
        controllingAPI.getControllingAreas(),
        controllingAPI.getProfitCenters(),
        hcmAPI.getEmployees().catch(() => ({ data: [] })),
        psAPI.getWBSProfiles()
      ]);
      setProjectProfiles(profilesRes.data.filter(p => p.status === 'Active'));
      setCompanyCodes(ccRes.data);
      setControllingAreas(caRes.data);
      setProfitCenters(pcRes.data);
      setEmployees(empRes.data || []);
      setWbsProfiles(wbsProfilesRes.data.filter(p => p.status === 'Active'));
    } catch (error) {
      console.error('Error loading lookup data:', error);
    }
  };

  const fetchProject = async () => {
    try {
      setLoading(true);
      const [projectRes, wbsRes] = await Promise.all([
        psAPI.getProject(projectId),
        psAPI.getWBSElements({ project_id: projectId })
      ]);
      const project = projectRes.data;
      setFormData({
        ...project,
        start_date: project.start_date?.split('T')[0] || '',
        end_date: project.end_date?.split('T')[0] || '',
        description: project.description || '',
        business_area_id: project.business_area_id || '',
        profit_center_id: project.profit_center_id || '',
        plant_id: project.plant_id || '',
        responsible_person_id: project.responsible_person_id || '',
        applicant_id: project.applicant_id || '',
        customer_id: project.customer_id || '',
        sales_order_id: project.sales_order_id || '',
        investment_program_id: project.investment_program_id || '',
        investment_reason_id: project.investment_reason_id || ''
      });
      setWbsElements(wbsRes.data);
    } catch (error) {
      toast.error('Failed to load project');
      navigate('/ps/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData.project_id || !formData.name || !formData.project_profile_id || !formData.company_code_id || !formData.controlling_area_id) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      setSaving(true);
      if (isEdit) {
        await psAPI.updateProject(projectId, formData);
        toast.success('Project updated successfully');
      } else {
        await psAPI.createProject(formData);
        toast.success('Project created successfully');
      }
      navigate('/ps/projects');
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'object') {
        toast.error(errorMsg.msg || JSON.stringify(errorMsg));
      } else {
        toast.error(errorMsg || 'Failed to save project');
      }
    } finally {
      setSaving(false);
    }
  };

  // WBS Functions
  const openWBSDialog = (parentId = null, editElement = null) => {
    if (editElement) {
      setEditWBS(true);
      setWbsFormData({
        ...editElement,
        description: editElement.description || '',
        parent_wbs_id: editElement.parent_wbs_id || '',
        wbs_profile_id: editElement.wbs_profile_id || '',
        start_date: editElement.start_date?.split('T')[0] || '',
        end_date: editElement.end_date?.split('T')[0] || '',
        sort_key: editElement.sort_key || ''
      });
    } else {
      setEditWBS(false);
      const parentLevel = parentId ? wbsElements.find(w => w.wbs_element_id === parentId)?.level || 0 : 0;
      setWbsFormData({
        wbs_element_id: '', name: '', description: '', project_id: projectId,
        parent_wbs_id: parentId || '', level: parentLevel + 1, sort_key: '',
        wbs_profile_id: '', is_account_assignment_element: true,
        is_planning_element: true, is_billing_element: false,
        start_date: formData?.start_date || '', 
        end_date: formData?.end_date || ''
      });
    }
    setShowWBSDialog(true);
  };

  const handleWBSSubmit = async () => {
    if (!wbsFormData.wbs_element_id || !wbsFormData.name) {
      toast.error('WBS Element ID and Name are required');
      return;
    }
    
    // Validate WBS Element ID format
    if (!/^[A-Z0-9-_]+$/.test(wbsFormData.wbs_element_id)) {
      toast.error('WBS Element ID must contain only uppercase letters, numbers, hyphens and underscores');
      return;
    }
    
    // Warn if no flags are enabled (element won't be useful for any operation)
    if (!wbsFormData.is_account_assignment_element && !wbsFormData.is_planning_element && !wbsFormData.is_billing_element) {
      toast.warning('Warning: This WBS element has no flags enabled. It cannot receive cost postings, plans, or billing.');
    }
    
    // Validate date consistency
    if (wbsFormData.start_date && wbsFormData.end_date && wbsFormData.start_date > wbsFormData.end_date) {
      toast.error('Start date cannot be after end date');
      return;
    }
    
    try {
      if (editWBS) {
        await psAPI.updateWBSElement(wbsFormData.wbs_element_id, wbsFormData);
        toast.success('WBS Element updated successfully');
      } else {
        await psAPI.createWBSElement({ ...wbsFormData, project_id: projectId });
        toast.success('WBS Element created successfully');
      }
      setShowWBSDialog(false);
      // Refresh WBS elements
      const wbsRes = await psAPI.getWBSElements({ project_id: projectId });
      setWbsElements(wbsRes.data);
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'object') {
        toast.error(errorMsg.msg || JSON.stringify(errorMsg));
      } else {
        toast.error(errorMsg || 'Failed to save WBS element');
      }
    }
  };

  const handleReleaseWBS = async (wbsId) => {
    try {
      await psAPI.releaseWBSElement(wbsId);
      toast.success('WBS Element released');
      const wbsRes = await psAPI.getWBSElements({ project_id: projectId });
      setWbsElements(wbsRes.data);
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'object' ? errorMsg.msg : errorMsg || 'Failed');
    }
  };

  const toggleWBS = (wbsId) => {
    const newExpanded = new Set(expandedWBS);
    if (newExpanded.has(wbsId)) newExpanded.delete(wbsId);
    else newExpanded.add(wbsId);
    setExpandedWBS(newExpanded);
  };

  const buildWBSTree = (parentId = null) => {
    return wbsElements
      .filter(w => (w.parent_wbs_id || null) === parentId)
      .sort((a, b) => (a.sort_key || '').localeCompare(b.sort_key || '') || a.wbs_element_id.localeCompare(b.wbs_element_id));
  };

  const getStatusBadge = (status) => {
    const config = {
      'CREATED': { bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400' },
      'RELEASED': { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
      'TECO': { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
      'CLOSED': { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' }
    };
    const c = config[status] || config['CREATED'];
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`}></span>
        {status}
      </span>
    );
  };

  const renderWBSRow = (element, depth = 0) => {
    const children = buildWBSTree(element.wbs_element_id);
    const hasChildren = children.length > 0;
    const isExpanded = expandedWBS.has(element.wbs_element_id);

    return (
      <React.Fragment key={element.wbs_element_id}>
        <TableRow className="group hover:bg-orange-50/50 transition-colors">
          <TableCell>
            <div className="flex items-center" style={{ paddingLeft: `${depth * 24}px` }}>
              {hasChildren ? (
                <Button variant="ghost" size="icon" className="h-6 w-6 mr-1" onClick={() => toggleWBS(element.wbs_element_id)}>
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </Button>
              ) : <span className="w-7" />}
              <Layers className="h-4 w-4 mr-2 text-slate-400" />
              <span className="font-mono text-sm font-medium text-orange-600">{element.wbs_element_id}</span>
            </div>
          </TableCell>
          <TableCell className="font-medium text-slate-900">{element.name}</TableCell>
          <TableCell>
            <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-slate-100 text-xs font-medium text-slate-600">
              {element.level}
            </span>
          </TableCell>
          <TableCell>
            <div className="flex gap-1">
              {element.is_account_assignment_element && <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">AA</Badge>}
              {element.is_planning_element && <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">Plan</Badge>}
              {element.is_billing_element && <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">Bill</Badge>}
            </div>
          </TableCell>
          <TableCell>{getStatusBadge(element.system_status)}</TableCell>
          <TableCell>
            <div className="flex gap-1 opacity-60 group-hover:opacity-100">
              <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-slate-200" onClick={() => openWBSDialog(null, element)} title="Edit">
                <Edit className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-orange-100" onClick={() => openWBSDialog(element.wbs_element_id)} title="Add Child">
                <Plus className="h-3.5 w-3.5 text-orange-600" />
              </Button>
              {element.system_status === 'CREATED' && (
                <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-green-100" onClick={() => handleReleaseWBS(element.wbs_element_id)} title="Release">
                  <Play className="h-3.5 w-3.5 text-green-600" />
                </Button>
              )}
            </div>
          </TableCell>
        </TableRow>
        {isExpanded && children.map(child => renderWBSRow(child, depth + 1))}
      </React.Fragment>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-[400px]">
          <div className="flex items-center gap-3 text-slate-500">
            <div className="animate-spin h-5 w-5 border-2 border-orange-600 border-t-transparent rounded-full"></div>
            Loading project...
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="project-form-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/ps/projects')} className="hover:bg-slate-100">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-600 rounded-lg">
                <FolderKanban className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-slate-900">
                    {isEdit ? formData.name || 'Edit Project' : 'Create Project'}
                  </h1>
                  {isEdit && getStatusBadge(formData.system_status)}
                </div>
                <p className="text-sm text-slate-500">
                  {isEdit ? formData.project_id : 'Define project master data and structure'}
                </p>
              </div>
            </div>
          </div>
          <Button onClick={handleSubmit} disabled={saving} data-testid="save-project-btn" className="bg-orange-600 hover:bg-orange-700">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Project'}
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="basic" className="space-y-6">
          <TabsList className="bg-slate-100 p-1">
            <TabsTrigger value="basic" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Briefcase className="h-4 w-4 mr-2" />
              Basic Data
            </TabsTrigger>
            <TabsTrigger value="org" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Building2 className="h-4 w-4 mr-2" />
              Organization
            </TabsTrigger>
            <TabsTrigger value="dates" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Calendar className="h-4 w-4 mr-2" />
              Dates
            </TabsTrigger>
            <TabsTrigger value="control" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Settings2 className="h-4 w-4 mr-2" />
              Control
            </TabsTrigger>
            {isEdit && (
              <TabsTrigger value="wbs" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
                <Layers className="h-4 w-4 mr-2" />
                WBS Structure
                <Badge variant="secondary" className="ml-2 bg-slate-200 text-slate-700">{wbsElements.length}</Badge>
              </TabsTrigger>
            )}
          </TabsList>

          {/* Basic Data Tab */}
          <TabsContent value="basic">
            <Card className="border-0 shadow-sm">
              <CardHeader className="border-b bg-slate-50/50">
                <CardTitle className="text-base font-semibold text-slate-700">Basic Data</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Project ID <span className="text-red-500">*</span></Label>
                    <Input 
                      value={formData.project_id} 
                      onChange={(e) => setFormData({...formData, project_id: e.target.value.toUpperCase()})} 
                      placeholder="e.g., PRJ-2025-001" 
                      disabled={isEdit} 
                      className="bg-slate-50 border-slate-200 focus:bg-white"
                      data-testid="project-id-input" 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Name <span className="text-red-500">*</span></Label>
                    <Input 
                      value={formData.name} 
                      onChange={(e) => setFormData({...formData, name: e.target.value})} 
                      placeholder="Project name" 
                      className="bg-slate-50 border-slate-200 focus:bg-white"
                      data-testid="project-name-input" 
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Description</Label>
                    <Input 
                      value={formData.description} 
                      onChange={(e) => setFormData({...formData, description: e.target.value})} 
                      placeholder="Project description" 
                      className="bg-slate-50 border-slate-200 focus:bg-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Project Profile <span className="text-red-500">*</span></Label>
                    <Select value={formData.project_profile_id} onValueChange={(v) => {
                      const selectedProfile = projectProfiles.find(p => p.profile_id === v);
                      setFormData(prev => ({
                        ...prev, 
                        project_profile_id: v,
                        // Auto-populate from profile if values exist
                        controlling_area_id: selectedProfile?.controlling_area_id || prev.controlling_area_id,
                        company_code_id: selectedProfile?.company_code_id || prev.company_code_id,
                        profit_center_id: selectedProfile?.profit_center_id || prev.profit_center_id,
                        business_area_id: selectedProfile?.business_area_id || prev.business_area_id
                      }));
                      if (selectedProfile?.controlling_area_id || selectedProfile?.company_code_id) {
                        toast.info('Controlling Area and Company Code populated from profile');
                      }
                    }}>
                      <SelectTrigger className="bg-slate-50 border-slate-200" data-testid="profile-select">
                        <SelectValue placeholder="Select profile" />
                      </SelectTrigger>
                      <SelectContent>
                        {projectProfiles.map(p => (
                          <SelectItem key={p.profile_id} value={p.profile_id}>
                            {p.profile_id} - {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Priority</Label>
                    <Select value={String(formData.priority)} onValueChange={(v) => setFormData({...formData, priority: parseInt(v)})}>
                      <SelectTrigger className="bg-slate-50 border-slate-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 - Highest</SelectItem>
                        <SelectItem value="2">2 - High</SelectItem>
                        <SelectItem value="3">3 - Medium</SelectItem>
                        <SelectItem value="4">4 - Low</SelectItem>
                        <SelectItem value="5">5 - Lowest</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Organization Tab */}
          <TabsContent value="org">
            <Card className="border-0 shadow-sm">
              <CardHeader className="border-b bg-slate-50/50">
                <CardTitle className="text-base font-semibold text-slate-700">Organizational Assignment</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Company Code <span className="text-red-500">*</span></Label>
                    <Select value={formData.company_code_id} onValueChange={(v) => setFormData({...formData, company_code_id: v})}>
                      <SelectTrigger className="bg-slate-50 border-slate-200" data-testid="company-code-select">
                        <SelectValue placeholder="Select company code" />
                      </SelectTrigger>
                      <SelectContent>
                        {companyCodes.map(cc => (
                          <SelectItem key={cc.company_code_id} value={cc.company_code_id}>
                            {cc.company_code_id} - {cc.company_code_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Controlling Area <span className="text-red-500">*</span></Label>
                    <Select value={formData.controlling_area_id} onValueChange={(v) => setFormData({...formData, controlling_area_id: v})}>
                      <SelectTrigger className="bg-slate-50 border-slate-200" data-testid="controlling-area-select">
                        <SelectValue placeholder="Select controlling area" />
                      </SelectTrigger>
                      <SelectContent>
                        {controllingAreas.map(ca => (
                          <SelectItem key={ca.controlling_area_id} value={ca.controlling_area_id}>
                            {ca.controlling_area_id} - {ca.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Profit Center</Label>
                    <Select value={formData.profit_center_id || "__none__"} onValueChange={(v) => setFormData({...formData, profit_center_id: v === "__none__" ? "" : v})}>
                      <SelectTrigger className="bg-slate-50 border-slate-200">
                        <SelectValue placeholder="Select profit center" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {profitCenters.map(pc => (
                          <SelectItem key={pc.profit_center_id} value={pc.profit_center_id}>
                            {pc.profit_center_id} - {pc.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Responsible Person</Label>
                    <Select value={formData.responsible_person_id || "__none__"} onValueChange={(v) => setFormData({...formData, responsible_person_id: v === "__none__" ? "" : v})}>
                      <SelectTrigger className="bg-slate-50 border-slate-200">
                        <SelectValue placeholder="Select responsible person" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {employees.map(emp => (
                          <SelectItem key={emp.employee_id} value={emp.employee_id}>
                            {emp.employee_id} - {emp.personal_data?.first_name} {emp.personal_data?.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Dates Tab */}
          <TabsContent value="dates">
            <Card className="border-0 shadow-sm">
              <CardHeader className="border-b bg-slate-50/50">
                <CardTitle className="text-base font-semibold text-slate-700">Project Dates</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Planned Start Date <span className="text-red-500">*</span></Label>
                    <Input 
                      type="date" 
                      value={formData.start_date} 
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})} 
                      className="bg-slate-50 border-slate-200 focus:bg-white"
                      data-testid="start-date-input" 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Planned End Date <span className="text-red-500">*</span></Label>
                    <Input 
                      type="date" 
                      value={formData.end_date} 
                      onChange={(e) => setFormData({...formData, end_date: e.target.value})} 
                      className="bg-slate-50 border-slate-200 focus:bg-white"
                      data-testid="end-date-input" 
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Control Tab */}
          <TabsContent value="control">
            <Card className="border-0 shadow-sm">
              <CardHeader className="border-b bg-slate-50/50">
                <CardTitle className="text-base font-semibold text-slate-700">Control Settings</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Currency</Label>
                    <Select value={formData.currency} onValueChange={(v) => setFormData({...formData, currency: v})}>
                      <SelectTrigger className="bg-slate-50 border-slate-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD - US Dollar</SelectItem>
                        <SelectItem value="EUR">EUR - Euro</SelectItem>
                        <SelectItem value="GBP">GBP - British Pound</SelectItem>
                        <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-lg">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Statistical Project</Label>
                      <p className="text-xs text-slate-500 mt-0.5">No actual cost postings allowed</p>
                    </div>
                    <Switch 
                      checked={formData.is_statistical} 
                      onCheckedChange={(v) => setFormData({...formData, is_statistical: v})} 
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* WBS Structure Tab (only in edit mode) */}
          {isEdit && (
            <TabsContent value="wbs">
              <Card className="border-0 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between border-b bg-slate-50/50">
                  <CardTitle className="text-base font-semibold text-slate-700">Work Breakdown Structure</CardTitle>
                  <Button onClick={() => openWBSDialog()} data-testid="add-wbs-btn" className="bg-orange-600 hover:bg-orange-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Add WBS Element
                  </Button>
                </CardHeader>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 hover:bg-slate-50">
                        <TableHead className="font-semibold text-slate-600">WBS Element ID</TableHead>
                        <TableHead className="font-semibold text-slate-600">Name</TableHead>
                        <TableHead className="font-semibold text-slate-600 w-20">Level</TableHead>
                        <TableHead className="font-semibold text-slate-600">Flags</TableHead>
                        <TableHead className="font-semibold text-slate-600">Status</TableHead>
                        <TableHead className="font-semibold text-slate-600 w-[120px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {wbsElements.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-12">
                            <div className="flex flex-col items-center gap-2">
                              <Layers className="h-10 w-10 text-slate-300" />
                              <p className="text-slate-500">No WBS elements yet</p>
                              <Button variant="outline" size="sm" onClick={() => openWBSDialog()}>
                                Add first WBS element
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        buildWBSTree(null).map(element => renderWBSRow(element, 0))
                      )}
                    </TableBody>
                  </Table>
                </div>
                {wbsElements.length > 0 && (
                  <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/50">
                    <p className="text-sm text-slate-500">
                      <span className="font-medium text-slate-700">{wbsElements.length}</span> WBS element(s)
                    </p>
                  </div>
                )}
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* WBS Dialog */}
        <Dialog open={showWBSDialog} onOpenChange={setShowWBSDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">{editWBS ? 'Edit' : 'Create'} WBS Element</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">WBS Element ID <span className="text-red-500">*</span></Label>
                <Input 
                  value={wbsFormData.wbs_element_id} 
                  onChange={(e) => setWbsFormData({...wbsFormData, wbs_element_id: e.target.value.toUpperCase()})} 
                  disabled={editWBS} 
                  placeholder="e.g., PRJ-001-PHASE1" 
                  className="bg-slate-50"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Name <span className="text-red-500">*</span></Label>
                <Input 
                  value={wbsFormData.name} 
                  onChange={(e) => setWbsFormData({...wbsFormData, name: e.target.value})} 
                  placeholder="WBS Element name" 
                  className="bg-slate-50"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label className="text-sm font-medium">Description</Label>
                <Input 
                  value={wbsFormData.description} 
                  onChange={(e) => setWbsFormData({...wbsFormData, description: e.target.value})} 
                  className="bg-slate-50"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Parent WBS</Label>
                <Select value={wbsFormData.parent_wbs_id || "__none__"} onValueChange={(v) => setWbsFormData({...wbsFormData, parent_wbs_id: v === "__none__" ? "" : v, level: v === "__none__" ? 1 : (wbsElements.find(w => w.wbs_element_id === v)?.level || 0) + 1})}>
                  <SelectTrigger className="bg-slate-50">
                    <SelectValue placeholder="None (Top Level)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None (Top Level)</SelectItem>
                    {wbsElements.filter(w => w.wbs_element_id !== wbsFormData.wbs_element_id).map(w => (
                      <SelectItem key={w.wbs_element_id} value={w.wbs_element_id}>
                        {w.wbs_element_id} - {w.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">WBS Profile</Label>
                <Select value={wbsFormData.wbs_profile_id || "__none__"} onValueChange={(v) => setWbsFormData({...wbsFormData, wbs_profile_id: v === "__none__" ? "" : v})}>
                  <SelectTrigger className="bg-slate-50">
                    <SelectValue placeholder="Select profile" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {wbsProfiles.map(p => (
                      <SelectItem key={p.profile_id} value={p.profile_id}>
                        {p.profile_id} - {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Start Date</Label>
                <Input 
                  type="date" 
                  value={wbsFormData.start_date} 
                  onChange={(e) => setWbsFormData({...wbsFormData, start_date: e.target.value})} 
                  className="bg-slate-50"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">End Date</Label>
                <Input 
                  type="date" 
                  value={wbsFormData.end_date} 
                  onChange={(e) => setWbsFormData({...wbsFormData, end_date: e.target.value})} 
                  className="bg-slate-50"
                />
              </div>
              <div className="col-span-2 space-y-4 pt-4 border-t">
                <Label className="text-sm font-semibold text-slate-700">Element Type Flags</Label>
                <div className="grid grid-cols-1 gap-3">
                  <div className="flex items-center justify-between p-4 bg-orange-50 border border-orange-100 rounded-lg">
                    <div className="flex-1">
                      <Label className="text-sm font-medium text-orange-700">Account Assignment Element</Label>
                      <p className="text-xs text-orange-600 mt-0.5">Enables actual cost postings and commitments to this WBS element</p>
                    </div>
                    <Switch checked={wbsFormData.is_account_assignment_element} onCheckedChange={(v) => setWbsFormData({...wbsFormData, is_account_assignment_element: v})} />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-green-50 border border-green-100 rounded-lg">
                    <div className="flex-1">
                      <Label className="text-sm font-medium text-green-700">Planning Element</Label>
                      <p className="text-xs text-green-600 mt-0.5">Enables cost planning and budget allocation to this WBS element</p>
                    </div>
                    <Switch checked={wbsFormData.is_planning_element} onCheckedChange={(v) => setWbsFormData({...wbsFormData, is_planning_element: v})} />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-orange-50 border border-orange-100 rounded-lg">
                    <div className="flex-1">
                      <Label className="text-sm font-medium text-orange-700">Billing Element</Label>
                      <p className="text-xs text-orange-600 mt-0.5">Enables revenue planning and customer billing from this WBS element</p>
                    </div>
                    <Switch checked={wbsFormData.is_billing_element} onCheckedChange={(v) => setWbsFormData({...wbsFormData, is_billing_element: v})} />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowWBSDialog(false)}>Cancel</Button>
              <Button onClick={handleWBSSubmit} className="bg-orange-600 hover:bg-orange-700">
                {editWBS ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ProjectForm;
